import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Loader2, Volume2, VolumeX } from "lucide-react";
import { BasilAlertTriangle } from "@/components/icons/basil";
import { PioneerHoloCard, DEFAULT_HOLO_CARD_BACK } from "@shared/components/PioneerHoloCard";
import { Badge } from "@/components/ui/Badge";
import { GlowButton } from "@/components/ui/GlowButton";
import { usePrefersReducedMotion } from "@/components/draw/usePrefersReducedMotion";
import { finishGlow } from "@/components/draw/finishGlow";
import { PackOpenVideo } from "@/components/draw/PackOpenVideo";
import { revealSfx, setSfxEnabled, sfxEnabled, unlockSfx } from "@/components/draw/revealSfx";
import { BRAND } from "@/lib/brandAssets";
import { cardArt2xSrc, cardArtSrc } from "@/lib/cardArt";
import { finishMeta, parseCardId, type CardFinishMeta } from "@/lib/cards";
import { HOME_AGENTS } from "@/lib/homeAgents";
import { cn } from "@shared/lib/utils";
import "@/components/draw/reveal.css";

/** One pulled card, precomputed against the pre-pull collection by DrawPage. */
export type RevealCard = {
  key: string;
  cardId: string;
  /** Card was not owned before this pull. */
  isNew: boolean;
  /** Running owned count including this copy — shown as "×N" on duplicates. */
  ownedCount: number;
};

type RevealOverlayProps = {
  open: boolean;
  /** Pull request in flight — pulsing pack state. */
  pending: boolean;
  cards: RevealCard[];
  error: string | null;
  onClose: () => void;
  /** 오프닝 시네마틱 영상의 팩 id — 미지정 시 엔트리 팩 */
  packId?: string;
};

type ResolvedCard = {
  meta: CardFinishMeta;
  name: string;
  portrait: string;
  /** 1x real-card art (grid tiles) — null when the combo has no asset. */
  art: string | null;
  /** @2x real-card art (large single reveal, up to 20rem) — null when missing. */
  art2x: string | null;
};

function resolveRevealCard(card: RevealCard): ResolvedCard {
  const { agentId, finish } = parseCardId(card.cardId);
  const agent = HOME_AGENTS.find((entry) => entry.agentId === agentId);
  return {
    meta: finishMeta(finish),
    name: agent?.name ?? agentId,
    portrait: agent?.portrait ?? DEFAULT_HOLO_CARD_BACK,
    art: cardArtSrc(agentId, finish),
    art2x: cardArt2xSrc(agentId, finish),
  };
}

/** 홀로 이펙트 → 연출 티어 (0 base ~ 3 hyper-rare) — 서스펜스·사운드·버스트 강도 */
const EFFECT_TIER: Record<string, number> = {
  basic: 0,
  "rainbow-holo": 1,
  "radiant-holo": 2,
  "hyper-rare": 3,
};

function effectTier(meta: CardFinishMeta): number {
  return EFFECT_TIER[meta.holoEffect] ?? 0;
}

function vibrate(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // 미지원 — 무시
  }
}

function OwnershipBadge({ card }: { card: RevealCard }) {
  return card.isNew ? <Badge tone="accent">New</Badge> : <Badge>Owned ×{card.ownedCount}</Badge>;
}

/** 레어 리빌 파티클 버스트 — 등급색 + 화이트 스파크, transition 기반(키프레임 불필요) */
function RarityBurst({ color, big }: { color: string; big?: boolean }) {
  const parts = useMemo(() => {
    const count = big ? 34 : 22;
    return Array.from({ length: count }, (_, i) => {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.5;
      const dist = 70 + Math.random() * (big ? 130 : 80);
      return {
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist,
        size: 2.5 + Math.random() * 4.5,
        delay: Math.random() * 110,
        dur: 620 + Math.random() * 420,
        white: i % 5 === 0,
      };
    });
  }, [big]);
  const [fired, setFired] = useState(false);

  useEffect(() => {
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setFired(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, []);

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      {parts.map((p, i) => (
        <span
          key={i}
          className="absolute left-1/2 top-1/2 rounded-full"
          style={{
            width: p.size,
            height: p.size,
            background: p.white ? "#fff" : color,
            transform: fired
              ? `translate(${p.x}px, ${p.y}px) scale(0.1)`
              : "translate(0px, 0px) scale(1)",
            opacity: fired ? 0 : 1,
            transition: `transform ${p.dur}ms cubic-bezier(0.1,0.7,0.3,1) ${p.delay}ms, opacity ${p.dur}ms ease-out ${p.delay}ms`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * 카드 1장 리빌 시퀀스 — 카드 백(등급색 글로우 누출) → 서스펜스(레어는 셰이크·
 * 지연 증가) → 3D 플립 → 착지(사운드·버스트·햅틱). `forceCount`가 오르면
 * 즉시 결과로 점프(성급한 탭 = 스킵). 카드 교체는 key 리마운트.
 */
function FlipReveal({
  card,
  resolved,
  reduced,
  forceCount,
  onDone,
}: {
  card: RevealCard;
  resolved: ResolvedCard;
  reduced: boolean;
  forceCount: number;
  onDone: () => void;
}) {
  const tier = effectTier(resolved.meta);
  const { finish } = parseCardId(card.cardId);
  const glow = finishGlow(finish);
  const suspenseMs = reduced ? 0 : tier <= 1 ? 380 : tier === 2 ? 1050 : 1600;

  const [phase, setPhase] = useState<"back" | "flip" | "front">(reduced ? "front" : "back");
  const [showFront, setShowFront] = useState(reduced);
  const [burst, setBurst] = useState(false);
  const timers = useRef<number[]>([]);
  const finished = useRef(false);

  // eslint 없이 안전한 최신 onDone 참조
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const finish3 = () => {
      if (finished.current) return;
      finished.current = true;
      setPhase("front");
      setShowFront(true);
      revealSfx.reveal(tier);
      if (tier === 3) {
        revealSfx.rareSting();
        vibrate([30, 40, 90]);
      } else if (tier === 2) {
        vibrate(25);
      }
      if (tier >= 2 && !reduced) setBurst(true);
      onDoneRef.current();
    };

    if (reduced) {
      finish3();
      return;
    }

    timers.current.push(
      window.setTimeout(() => {
        setPhase("flip");
        revealSfx.flip();
        // 90° 시점(에지온)에서 백→프론트 스왑 — backface 미신뢰 크로스 플립
        timers.current.push(window.setTimeout(() => setShowFront(true), 300));
        timers.current.push(window.setTimeout(finish3, 640));
      }, suspenseMs),
    );

    return () => {
      for (const id of timers.current) window.clearTimeout(id);
      timers.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 성급한 탭 — 진행 중이면 즉시 착지
  useEffect(() => {
    if (forceCount === 0 || finished.current) return;
    for (const id of timers.current) window.clearTimeout(id);
    timers.current = [];
    finished.current = true;
    setPhase("front");
    setShowFront(true);
    revealSfx.reveal(tier);
    if (tier === 3) revealSfx.rareSting();
    onDoneRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceCount]);

  return (
    <div className={cn("relative w-full", phase === "back" && tier >= 2 && "ty-rare-shake")}>
      {/* 서스펜스 글로우 — 리빌 전 등급색이 카드 뒤에서 숨쉰다 (희귀도 예고) */}
      {phase !== "front" && (
        <div
          aria-hidden
          className="ty-suspense-glow pointer-events-none absolute left-1/2 top-1/2 h-[110%] w-[110%] rounded-full blur-3xl"
          style={{ background: glow }}
        />
      )}

      <div className="relative z-[1] w-full [perspective:1400px]">
        <div
          className="relative w-full transition-transform duration-[640ms] [transform-style:preserve-3d]"
          style={{
            transform: phase === "back" ? "rotateY(180deg)" : "rotateY(0deg)",
            transitionTimingFunction: "cubic-bezier(0.2, 0.75, 0.25, 1)",
          }}
        >
          {showFront ? (
            <PioneerHoloCard
              imageSrc={resolved.art2x ?? resolved.art ?? resolved.portrait}
              imageAlt={`${resolved.name} — ${resolved.meta.label}`}
              effect={resolved.meta.holoEffect}
              idleShimmer={phase === "front" && !reduced}
            />
          ) : (
            /* 카드 백 — 래퍼가 180° 상태라 내부 카운터 회전으로 정면 표시 */
            <div
              className="relative w-full overflow-hidden rounded-[4.6%/3.4%]"
              style={{ aspectRatio: "723 / 1024", transform: "rotateY(180deg)" }}
            >
              <img
                src={DEFAULT_HOLO_CARD_BACK}
                alt=""
                aria-hidden
                draggable={false}
                className="h-full w-full object-cover"
              />
              <div
                aria-hidden
                className="absolute inset-0 rounded-[inherit]"
                style={{
                  boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.14), 0 0 44px 6px ${glow}`,
                }}
              />
            </div>
          )}
        </div>
      </div>

      {burst && <RarityBurst color={glow} big={tier === 3} />}
    </div>
  );
}

/** Mount-time scale/fade entrance — 그리드 타일 스태거(delay)용. */
function GridEntrance({
  animate,
  delay = 0,
  children,
}: {
  animate: boolean;
  delay?: number;
  children: ReactNode;
}) {
  const [entered, setEntered] = useState(!animate);

  useEffect(() => {
    if (!animate) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [animate]);

  return (
    <div
      className={cn(
        "transition-all duration-500 ease-out",
        entered ? "translate-y-0 scale-100 opacity-100" : "translate-y-3 scale-90 opacity-0",
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/** After this long in `pending`, ESC unblocks and a ghost Cancel appears. */
const PENDING_ESCAPE_MS = 15_000;

/**
 * Draw §6 — full-screen pack-open overlay.
 * 페이즈: pending(펄스 팩) → cinema(Blender 영상, 유저가 찢음) → reveal(카드별
 * 플립 탭스루) → summary(스태거 그리드 + Top pull 하이라이트). SFX는 WebAudio
 * 신스(revealSfx), 우상단 토글로 on/off.
 */
export function RevealOverlay({ open, pending, cards, error, onClose, packId }: RevealOverlayProps) {
  const reducedMotion = usePrefersReducedMotion();
  const [shown, setShown] = useState(1);
  const [stage, setStage] = useState<"single" | "grid">("single");
  // The pull RPC has no client-side timeout — after 15s of pending we surface
  // an escape hatch so the dialog can't hang forever.
  const [pendingTimedOut, setPendingTimedOut] = useState(false);
  const [cinemaDone, setCinemaDone] = useState(false);
  const [whiteFade, setWhiteFade] = useState(false);
  const [currentDone, setCurrentDone] = useState(false);
  const [forceCount, setForceCount] = useState(0);
  const [soundOn, setSoundOn] = useState(() => sfxEnabled());
  const dialogRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  const multi = cards.length > 1;

  // Reset the walkthrough whenever a new result lands; reduced motion jumps straight to the summary.
  useEffect(() => {
    if (!open) return;
    if (reducedMotion && cards.length > 1) {
      setStage("grid");
      setShown(cards.length);
    } else {
      setStage("single");
      setShown(1);
    }
    setCinemaDone(reducedMotion); // 모션 최소화 설정은 시네마틱 생략
    setWhiteFade(false);
  }, [open, cards, reducedMotion]);

  // 카드가 바뀔 때마다 리빌 완료/스킵 카운터 리셋
  useEffect(() => {
    setCurrentDone(false);
    setForceCount(0);
  }, [open, cards, shown]);

  // 화이트아웃 → 카드 리빌 전환: 흰 화면이 카드 위에서 사그라듦
  useEffect(() => {
    if (!whiteFade) return;
    const id = window.setTimeout(() => setWhiteFade(false), 60);
    return () => window.clearTimeout(id);
  }, [whiteFade]);

  // 오프닝 영상 워밍 — 풀 RPC가 나는 동안 두 변형(normal/rare)을 미리 버퍼링해
  // 시네마틱 콜드스타트 제거 (페이지 prefetch 미적중·캐시 축출 대비 이중 안전망).
  useEffect(() => {
    if (!open || !pending) return;
    const id = packId ?? "ember";
    const warmers = [
      `/videos/pack-open/${id}.mp4?v=2`,
      `/videos/pack-open/${id}.rare.mp4?v=2`,
    ].map((src) => {
      const video = document.createElement("video");
      video.preload = "auto";
      video.muted = true;
      video.src = src;
      video.load();
      return video;
    });
    return () => {
      // 닫힘/전환 시 버퍼링 중단 — 네트워크 낭비 방지
      for (const video of warmers) {
        video.removeAttribute("src");
        video.load();
      }
    };
  }, [open, pending, packId]);

  // 리빌 아트 프리로드 — 시네마틱이 도는 동안 @2x 아트를 받아 플립 팝인 제거
  useEffect(() => {
    if (!open || pending || cards.length === 0) return;
    for (const card of cards) {
      const resolved = resolveRevealCard(card);
      const img = new Image();
      img.src = resolved.art2x ?? resolved.art ?? resolved.portrait;
    }
  }, [open, pending, cards]);

  // While a pull is in flight, arm the 15s escape hatch; reset once it lands.
  useEffect(() => {
    if (!open || !pending) {
      setPendingTimedOut(false);
      return;
    }
    const id = window.setTimeout(() => setPendingTimedOut(true), PENDING_ESCAPE_MS);
    return () => window.clearTimeout(id);
  }, [open, pending]);

  // Body scroll lock + Escape (blocked only during the first 15s of pending).
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && (!pending || pendingTimedOut)) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, pending, pendingTimedOut, onClose]);

  const showError = Boolean(error) && !pending;
  const cardsReady = !pending && !showError && cards.length > 0;
  const showCinema = cardsReady && !cinemaDone;
  const showCards = cardsReady && cinemaDone;
  const hasNext = multi && shown < cards.length;

  // 요약 그리드 진입 — 종결음 1회
  useEffect(() => {
    if (open && showCards && stage === "grid") revealSfx.summary();
  }, [open, showCards, stage]);

  // Move focus into the dialog on open and on phase changes — the footer
  // primary button when present, else the dialog root (pending spinner).
  useEffect(() => {
    if (!open) return;
    const primary = footerRef.current?.querySelector("button");
    if (primary) primary.focus();
    else dialogRef.current?.focus();
  }, [open, pending, pendingTimedOut, showError, stage, hasNext]);

  // Trap Tab inside the dialog while open.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.tabIndex >= 0);
      if (focusables.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !dialog.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Top pull — 최고 티어(동률이면 pullIp 높은 쪽) 인덱스, 요약 그리드 하이라이트
  const topIndex = useMemo(() => {
    let best = -1;
    let bestScore = -1;
    cards.forEach((card, i) => {
      const meta = finishMeta(parseCardId(card.cardId).finish);
      const score = effectTier(meta) * 1000 + meta.pullIp;
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    });
    return best;
  }, [cards]);

  if (!open) return null;

  const current = cards[Math.min(shown, cards.length) - 1];
  const resolved = current ? resolveRevealCard(current) : null;
  const newCount = cards.filter((card) => card.isNew).length;

  const advance = () => {
    if (pending || showError) return;
    // 리빌 진행 중 탭 = 즉시 결과(스킵), 완료 후 탭 = 다음 카드/그리드
    if (stage === "single" && !currentDone) {
      setForceCount((count) => count + 1);
      return;
    }
    if (hasNext) setShown((count) => count + 1);
    else if (multi && stage === "single") setStage("grid");
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setSfxEnabled(next);
    if (next) unlockSfx();
  };

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={pending ? "Opening pack" : "Card reveal"}
      tabIndex={-1}
      className="fixed inset-0 z-[80] flex flex-col outline-none"
    >
      {/* 백드롭 — 임의 퍼센트 오패시티 클래스 미적용 방지, 명시 rgba */}
      <div className="absolute inset-0 bg-[rgba(0,0,0,0.94)]" aria-hidden />

      {/* 화이트아웃 잔광 — 시네마틱이 흰 화면으로 끝나면 카드 위에서 페이드아웃 */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 z-20 bg-white",
          whiteFade ? "opacity-100" : "opacity-0 transition-opacity duration-700",
        )}
      />

      {/* SFX 토글 — 게임 오버레이 공통 문법, 전 페이즈 우상단 */}
      {!pending && (
        <button
          type="button"
          onClick={toggleSound}
          aria-label={soundOn ? "Mute sound effects" : "Unmute sound effects"}
          className="absolute right-4 top-[max(1rem,env(safe-area-inset-top))] z-30 grid size-9 place-items-center rounded-full border border-white/15 bg-black/40 text-white/70 transition-colors duration-200 hover:border-white/30 hover:text-white"
        >
          {soundOn ? <Volume2 className="size-4" aria-hidden /> : <VolumeX className="size-4" aria-hidden />}
        </button>
      )}

      {/* Ambient glow tinted by the revealed finish */}
      {showCards && stage === "single" && resolved ? (
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 size-[70vmin] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[110px] transition-colors duration-700"
          style={{ background: finishGlow(resolved.meta.finish) }}
        />
      ) : null}

      {/* Stage */}
      <div
        className="relative z-[1] flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden px-4 pt-[max(1rem,env(safe-area-inset-top))]"
        onClick={showCards && stage === "single" ? advance : undefined}
      >
        {pending ? (
          <div className="flex flex-col items-center gap-6">
            <div className="relative w-36 sm:w-44">
              <div
                aria-hidden
                className="absolute inset-0 -m-10 animate-pulse rounded-full bg-accent/30 blur-3xl motion-reduce:animate-none"
              />
              <img
                src={BRAND.cardPack}
                alt=""
                className="relative w-full animate-pulse drop-shadow-[0_16px_40px_rgba(0,0,0,0.8)] motion-reduce:animate-none"
              />
            </div>
            <p className="flex items-center gap-2 font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-white/70">
              <Loader2 className="size-4 animate-spin text-accent motion-reduce:animate-none" aria-hidden />
              Opening pack…
            </p>
          </div>
        ) : showCinema ? (
          <div className="relative h-full w-full">
            <PackOpenVideo
              packId={packId ?? "ember"}
              rare={cards.some((card) => effectTier(finishMeta(parseCardId(card.cardId).finish)) >= 2)}
              onTear={() => {
                unlockSfx();
                revealSfx.tear();
                vibrate(18);
              }}
              onDone={() => {
                setCinemaDone(true);
                setWhiteFade(true);
              }}
            />
            <button
              type="button"
              onClick={() => setCinemaDone(true)}
              className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full border border-white/15 px-4 py-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-white/60 transition-colors duration-200 hover:text-white"
            >
              Skip
            </button>
          </div>
        ) : showError ? (
          <div className="flex max-w-sm flex-col items-center gap-4 text-center">
            <span className="grid size-12 place-items-center rounded-full border border-danger/30 bg-danger/15">
              <BasilAlertTriangle className="size-5 text-danger" aria-hidden />
            </span>
            <p className="text-sm font-semibold leading-relaxed text-danger">{error}</p>
          </div>
        ) : showCards && stage === "single" && current && resolved ? (
          <div className="flex w-full max-w-[17rem] flex-col items-center gap-4 sm:max-w-xs">
            <FlipReveal
              key={current.key}
              card={current}
              resolved={resolved}
              reduced={reducedMotion}
              forceCount={forceCount}
              onDone={() => setCurrentDone(true)}
            />

            {/* 카드 정보 — 리빌 완료 후 페이드업 */}
            <div
              className={cn(
                "flex flex-col items-center gap-1.5 transition-all duration-300",
                currentDone ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
              )}
            >
              <div className="flex items-center gap-2">
                <p className={cn("font-mono text-xs font-medium uppercase tracking-[0.14em]", resolved.meta.accentText)}>
                  {resolved.meta.label}
                </p>
                <OwnershipBadge card={current} />
              </div>
              <p className="font-display text-lg font-semibold text-ink">{resolved.name}</p>
            </div>

            {/* 멀티 풀 진행 — 등급색 도트 레일(공개된 카드는 등급색 점화) */}
            {multi && (
              <div className="flex items-center gap-2" aria-label={`Card ${shown} of ${cards.length}`}>
                {cards.map((card, i) => {
                  const done = i < shown - 1 || (i === shown - 1 && currentDone);
                  const isCurrent = i === shown - 1;
                  const glow = finishGlow(parseCardId(card.cardId).finish);
                  return (
                    <span
                      key={card.key}
                      className={cn(
                        "size-1.5 rounded-full transition-all duration-300",
                        isCurrent && !done && "scale-125 bg-white/80 motion-safe:animate-pulse",
                        !done && !isCurrent && "bg-white/15",
                      )}
                      style={done ? { background: glow, boxShadow: `0 0 6px ${glow}` } : undefined}
                    />
                  );
                })}
              </div>
            )}
          </div>
        ) : showCards ? (
          <div className="flex max-h-full min-h-0 w-full max-w-2xl flex-col items-center gap-3">
            <p className="ty-eyebrow text-[10px]">
              Pull results — {cards.length} card{cards.length === 1 ? "" : "s"}
              {newCount > 0 ? ` · ${newCount} new` : ""}
            </p>
            <div className="grid w-full grid-cols-2 gap-3 overflow-y-auto px-1 py-2 min-[480px]:grid-cols-3 sm:grid-cols-5">
              {cards.map((card, index) => {
                const entry = resolveRevealCard(card);
                const top = index === topIndex && multi;
                return (
                  <GridEntrance key={card.key} animate={!reducedMotion} delay={index * 55}>
                    {/* UF-4 진입점 — 개봉 직후가 카드 확인 동기 최고점: 타일 → 카드 상세 */}
                    <Link
                      to={`/cards/${encodeURIComponent(card.cardId)}`}
                      onClick={onClose}
                      className="flex min-w-0 flex-col items-center gap-1.5 rounded-lg outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70"
                    >
                      <div className={cn("relative w-full", top && "rounded-[6px]")}>
                        {/* Top pull — 최고 등급 글로우 링 + 칩 */}
                        {top && (
                          <>
                            <div
                              aria-hidden
                              className="pointer-events-none absolute -inset-1 rounded-[8px]"
                              style={{
                                boxShadow: `0 0 0 1.5px ${finishGlow(entry.meta.finish)}, 0 0 24px 2px ${finishGlow(entry.meta.finish)}`,
                              }}
                            />
                            <span className="absolute -top-2 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full border border-(--ty-line-gold) bg-(--ty-bg) px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.12em] text-accent">
                              Top pull
                            </span>
                          </>
                        )}
                        <PioneerHoloCard
                          imageSrc={entry.art ?? entry.portrait}
                          imageAlt={`${entry.name} — ${entry.meta.label}`}
                          effect={entry.meta.holoEffect}
                          interactive={false}
                          muted
                          idleOpacityScale={0.5}
                          loading="lazy"
                        />
                      </div>
                      <OwnershipBadge card={card} />
                      <p className={cn("w-full truncate text-center font-mono text-[9px] font-medium uppercase tracking-[0.08em]", entry.meta.accentText)}>
                        {entry.meta.label}
                      </p>
                    </Link>
                  </GridEntrance>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {/* Footer actions — 시네마틱 동안은 비움(스킵은 스테이지 안) */}
      <div
        ref={footerRef}
        className="relative z-[1] flex shrink-0 justify-center px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3"
      >
        {pending ? (
          // Escape hatch for a hung pull. Closing only dismisses the UI — the
          // RPC may still resolve server-side (the pull can land regardless).
          // usePackReveal tracks this as "dismissed": if it does land, the
          // balance/cards still patch in silently and a toast tells the user
          // instead of popping this overlay back open unprompted — so the
          // button copy has to be honest that closing isn't the same as canceling.
          pendingTimedOut ? (
            <GlowButton variant="ghost" onClick={onClose} className="w-full max-w-xs">
              Hide — this may still complete
            </GlowButton>
          ) : null
        ) : showCinema ? null : showError ? (
          <GlowButton variant="ghost" onClick={onClose} className="w-full max-w-xs">
            Close
          </GlowButton>
        ) : stage === "single" && !currentDone ? (
          <GlowButton onClick={advance} className="w-full max-w-xs">
            Reveal now
          </GlowButton>
        ) : hasNext ? (
          <GlowButton onClick={advance} className="w-full max-w-xs">
            Reveal next
          </GlowButton>
        ) : multi && stage === "single" ? (
          <GlowButton onClick={advance} className="w-full max-w-xs">
            See all {cards.length} cards
          </GlowButton>
        ) : (
          /* UF-3 ⑦ — 닫기로 끝내지 않고 다음 행동(컬렉션 확인) 제안 */
          <div className="flex w-full max-w-md items-center gap-3">
            <GlowButton
              variant="ghost"
              to="/profile/collection"
              onClick={onClose}
              className="flex-1"
            >
              View in collection
            </GlowButton>
            <GlowButton onClick={onClose} className="flex-1">
              Continue
            </GlowButton>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
